import { Alert, Badge, Button, Code, Group, Select, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { listAdminAuditEvents, type AuditEvent } from "@/api/portal";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Admin UI — cross-region audit-log viewer (phase2-admin-ui).
 *
 * The staff counterpart to the tenant-locked Client Portal audit viewer: it
 * carries the full filter set (organization, service, patient) and is NOT pinned
 * to one tenant. Enforcement is downstream (the audit service scopes a tenant
 * token to its own org and gates a patient-id search on the PHI permission), so
 * this page is a superset UI — a staff user gets the fleet view; leave
 * Organization blank for all tenants.
 *
 * `phi_visible` is resolved by the BFF from IAM, not the token — an empty table
 * without the PHI permission means "you are not being shown the alerts", not
 * "there are none" (same safety contract as the portal viewer, ADR-0051).
 */

const PRESETS = {
  critical: { eventType: "notification.trigger.", severity: "critical" },
  triggers: { eventType: "notification.trigger.", severity: undefined },
  everything: { eventType: undefined, severity: undefined },
} as const;

type PresetKey = keyof typeof PRESETS;

const WINDOW_HOURS: Record<string, number> = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 };

const SEVERITY_COLOR: Record<string, string> = {
  critical: "red",
  urgent: "orange",
  warn: "yellow",
  info: "gray",
};

export function AdminAuditPage(): JSX.Element {
  const [preset, setPreset] = useState<PresetKey>("critical");
  const [timeWindow, setTimeWindow] = useState("7d");
  // Free-text filters are staged in local state and only committed to `applied`
  // on Search, so typing doesn't fire a query per keystroke.
  const [orgInput, setOrgInput] = useState("");
  const [serviceInput, setServiceInput] = useState("");
  const [patientInput, setPatientInput] = useState("");
  const [eventInput, setEventInput] = useState("");
  const [applied, setApplied] = useState({ org: "", service: "", patient: "", event: "" });

  const nowMinute = Math.floor(Date.now() / 60_000);
  const since = new Date(
    nowMinute * 60_000 - (WINDOW_HOURS[timeWindow] ?? 168) * 60 * 60 * 1000,
  ).toISOString();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["admin-audit", preset, timeWindow, nowMinute, applied],
    queryFn: () =>
      listAdminAuditEvents({
        // an explicit event-type filter overrides the preset's prefix
        eventType: applied.event || PRESETS[preset].eventType,
        severity: PRESETS[preset].severity,
        since,
        organizationId: applied.org || undefined,
        service: applied.service || undefined,
        patientId: applied.patient || undefined,
        limit: 200,
      }),
    retry: false,
    refetchInterval: 60_000,
  });

  const canSeePhi = data?.phi_visible ?? false;

  return (
    <AppLayout breadcrumbs={[{ label: t("admin.audit_title") }]}>
      <Title order={1}>{t("admin.audit_title")}</Title>
      <Text c="dimmed" mt="xs">
        {t("admin.audit_subtitle")}
      </Text>

      <Group mt="md" mb="xs" align="flex-end">
        <Select
          label={t("portal.audit_preset")}
          data={[
            { value: "critical", label: t("portal.audit_preset_critical") },
            { value: "triggers", label: t("portal.audit_preset_all_triggers") },
            { value: "everything", label: t("portal.audit_preset_everything") },
          ]}
          value={preset}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) setPreset(v as PresetKey);
          }}
        />
        <Select
          label={t("portal.audit_window")}
          data={[
            { value: "24h", label: t("portal.audit_window_24h") },
            { value: "7d", label: t("portal.audit_window_7d") },
            { value: "30d", label: t("portal.audit_window_30d") },
          ]}
          value={timeWindow}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) setTimeWindow(v);
          }}
        />
      </Group>

      <Group mb="md" align="flex-end">
        <TextInput
          label={t("admin.audit_filter_org")}
          placeholder={t("admin.audit_filter_org_placeholder")}
          value={orgInput}
          onChange={(e) => setOrgInput(e.currentTarget.value)}
        />
        <TextInput
          label={t("admin.audit_filter_service")}
          value={serviceInput}
          onChange={(e) => setServiceInput(e.currentTarget.value)}
        />
        <TextInput
          label={t("admin.audit_filter_patient")}
          value={patientInput}
          onChange={(e) => setPatientInput(e.currentTarget.value)}
        />
        <TextInput
          label={t("admin.audit_filter_event")}
          value={eventInput}
          onChange={(e) => setEventInput(e.currentTarget.value)}
        />
        <Button
          onClick={() =>
            setApplied({
              org: orgInput.trim(),
              service: serviceInput.trim(),
              patient: patientInput.trim(),
              event: eventInput.trim(),
            })
          }
        >
          {t("admin.audit_apply")}
        </Button>
      </Group>

      {!canSeePhi && (
        <Alert color="yellow" mb="md" title={t("portal.audit_phi_title")}>
          <Text size="sm">{t("portal.audit_phi_note")}</Text>
        </Alert>
      )}

      {isPending ? (
        <TableSkeleton label={t("portal.audit_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("portal.audit_unavailable")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : data.events.length === 0 ? (
        <Text c="dimmed">{t("portal.audit_empty")}</Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {data.count} {t("portal.audit_count_suffix")}
          </Text>
          <EventTable events={data.events} />
        </>
      )}
    </AppLayout>
  );
}

function EventTable({ events }: { events: AuditEvent[] }): JSX.Element {
  return (
    <Table striped highlightOnHover withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.audit_col_time")}</Table.Th>
          <Table.Th>{t("admin.audit_col_org")}</Table.Th>
          <Table.Th>{t("portal.audit_col_event")}</Table.Th>
          <Table.Th>{t("portal.audit_col_severity")}</Table.Th>
          <Table.Th>{t("portal.audit_col_patient")}</Table.Th>
          <Table.Th>{t("portal.audit_col_service")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((event) => (
          <Table.Tr key={event.event_id}>
            <Table.Td>{event.occurred_at ?? event.received_at ?? "—"}</Table.Td>
            <Table.Td>{event.organization_id ?? "—"}</Table.Td>
            <Table.Td>
              <Code>{event.event_type}</Code>
            </Table.Td>
            <Table.Td>
              <Badge color={SEVERITY_COLOR[event.severity.toLowerCase()] ?? "blue"} variant="light">
                {event.severity}
              </Badge>
            </Table.Td>
            <Table.Td>{event.patient_id ?? "—"}</Table.Td>
            <Table.Td>{event.service}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
